'use client';

import { FC, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { MapPin, Clock, LoaderCircle } from 'lucide-react';
import { SuggestedCity } from '@/hotdesk-ui/store/slices/suggested-cities.slice';
import { RecentSearch } from '@/hotdesk-ui/helpers/recent-searches';
import styles from './SuggestedDestinations.module.scss';
export interface SuggestedDestinationsProps {
  cities: SuggestedCity[];
  loading?: boolean;
  /** City the visitor is currently in — highlighted in the list. */
  currentCity?: string;
  onSelect: (city: SuggestedCity) => void;
  /** "Use my location" — the Nearby row's action. */
  onUseMyLocation: () => void;
  /** The Nearby row is waiting on the device's position. */
  nearbyLoading?: boolean;
  /** Places picked previously, newest first. Rendered above the suggestions. */
  recent?: RecentSearch[];
  onSelectRecent?: (entry: RecentSearch) => void;
  onClearRecent?: () => void;
}
const TAGLINE_KEYS = ['tagline1', 'tagline2', 'tagline3', 'tagline4'];

/**
 * Hands each city a different tagline. The order is shuffled from a seed
 * derived from the city ids rather than `Math.random()`, so a given set of
 * cities always gets the same captions instead of reshuffling every time the
 * panel reopens. With more cities than taglines the pool wraps and repeats.
 */
function assignTaglines(cities: SuggestedCity[]): string[] {
  let seed = cities.reduce((acc, city) => acc * 31 + city.id >>> 0, 7);
  const nextRandom = () => {
    seed = Math.imul(seed, 1664525) + 1013904223 >>> 0;
    return seed / 4294967296;
  };
  const pool = [...TAGLINE_KEYS];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(nextRandom() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  return cities.map((_, index) => pool[index % pool.length]);
}

/**
 * Shown when the search input is focused but empty: "Nearby" plus a handful of
 * cities in the visitor's country, so there is something to click before they
 * type.
 */
const SuggestedDestinations: FC<SuggestedDestinationsProps> = ({
  cities,
  loading = false,
  currentCity,
  onSelect,
  onUseMyLocation,
  nearbyLoading = false,
  recent = [],
  onSelectRecent,
  onClearRecent
}) => {
  const t = useTranslations('landing-v4.search');
  const normalisedCurrent = currentCity?.trim().toLowerCase();
  const taglines = useMemo(() => assignTaglines(cities), [cities]);
  return <div className={styles.card} role="listbox" data-sentry-component="SuggestedDestinations" data-sentry-source-file="SuggestedDestinations.tsx">
            <div className={styles.cardScroll}>
            {recent.length > 0 && <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <span>{t('recentSearches')}</span>
                        {onClearRecent && <button type="button" className={styles.clearRecent} onMouseDown={e => {
            e.preventDefault();
            onClearRecent();
          }}>
                                {t('clearRecent')}
                            </button>}
                    </div>

                    {recent.map(entry => <div key={entry.id} role="option" aria-selected={false} tabIndex={0} className={styles.row} onMouseDown={e => {
          e.preventDefault();
          onSelectRecent?.(entry);
        }} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectRecent?.(entry);
          }
        }}>
                            <span className={styles.recentIcon}>
                                <Clock size={18} strokeWidth={1.75} />
                            </span>
                            <span className={styles.textStack}>
                                <span className={styles.cityName}>
                                    {entry.label}
                                </span>
                            </span>
                        </div>)}
                </div>}

            <div className={`${styles.section} ${recent.length > 0 ? styles.sectionAfterRecent : ''}`}>
                <div className={styles.sectionHeader}>
                    {t('suggestedDestinations')}
                </div>

                <div role="option" aria-selected={false} aria-busy={nearbyLoading} tabIndex={0} className={`${styles.row} ${nearbyLoading ? styles.rowBusy : ''}`} onMouseDown={e => {
          // Keep focus on the input; act before its blur.
          e.preventDefault();
          if (!nearbyLoading) onUseMyLocation();
        }} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!nearbyLoading) onUseMyLocation();
          }
        }}>
                    <span className={styles.nearbyIcon}>
                        {nearbyLoading ? <LoaderCircle size={20} strokeWidth={1.75} className={styles.spin} /> : <MapPin size={20} strokeWidth={1.75} />}
                    </span>
                    <span className={styles.textStack}>
                        <span className={styles.cityName}>{t('nearby')}</span>
                        <span className={styles.cityCaption}>
                            {t(nearbyLoading ? 'nearbyLoading' : 'nearbyCaption')}
                        </span>
                    </span>
                </div>

                {loading ? [0, 1, 2, 3].map(i => <div key={i} className={styles.row} aria-hidden="true">
                              <span className={styles.textStack}>
                                  <span className={`${styles.shimmerLine} ${styles.shimmer}`} style={{
              width: '52%'
            }} />
                                  <span className={`${styles.shimmerLine} ${styles.shimmer}`} style={{
              width: '32%'
            }} />
                              </span>
                          </div>) : cities.map((city, index) => <div key={city.id} role="option" aria-selected={false} tabIndex={0} className={`${styles.row} ${normalisedCurrent && city.name.trim().toLowerCase() === normalisedCurrent ? styles.rowActive : ''}`} onMouseDown={e => {
          e.preventDefault();
          onSelect(city);
        }} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect(city);
          }
        }}>
                              <span className={styles.textStack}>
                                  <span className={styles.cityName}>
                                      {city.countryName ? `${city.name}, ${city.countryName}` : city.name}
                                  </span>
                                  <span className={styles.cityCaption}>
                                      {t(taglines[index])}
                                  </span>
                              </span>
                          </div>)}
            </div>
            </div>
        </div>;
};
export default SuggestedDestinations;